import { Button } from '@teach/ui'
import { resumeFailureText } from '@/features/ai/lib/resumeFailureText'
import { failureText } from '@/features/ai/lib/failureText'
import type { StartFailure } from '@/features/ai/lib/parseStartError'
import type { ResumeHint } from '@/features/ai/studioTypes'
import { messagesNow, useMessages } from '@/i18n'
import { messages } from './sidebarMessages'

interface Props {
  hint: ResumeHint
  /** Последний фолбэк, когда причину `hint.reason` не знает НИ `resumeFailureText`,
   *  НИ `businessView` (см. `hintText` ниже). Печатается как есть, вместо
   *  выдуманного нами диагноза — та же философия, что у соседних таблиц
   *  текстов (`failureText.ts` и т.п.). */
  errorMessage: string | null
  onResume?: () => void
  /** `reason: "already_resumed"` — id продолжения уже известен, сеть не
   *  нужна (§8, финальное ревью п.5). */
  onShowResumed?: (jobId: number) => void
  isResuming: boolean
  /** Отказ САМОЙ ручки продолжения (нажали и не вышло) — в отличие от
   *  `hint.reason`, который объясняет, почему кнопки нет. */
  resumeFailure: StartFailure | null
  /** Курс на проверке: продолжение пишет в курс, обе кнопки гаснут с причиной. */
  isFrozen?: boolean
  /** Успешный прогон пропустил только окончательное — «Продолжить»/«Попробовать
   *  снова» спрятаны (повтор упрётся в то же). `show-resumed` не трогается. */
  skipsAllFinal?: boolean
}

type ButtonKind = 'none' | 'continue' | 'retry' | 'show-resumed'

/**
 * Какую кнопку рисовать — отдельно от JSX, чтобы ветвление (доступно /
 * нечем продолжить / чужой прогон / уже продолжен где-то ещё) не поднимало
 * eslint `complexity` самого компонента.
 *
 * `already_resumed` — первая проверка: `job_not_yours` и `available` у неё
 * не бывает содержательными (§8), а `job_id` называет живое продолжение
 * прямо — рисовать «Попробовать снова» и звать ту же ручку означало бы
 * заведомо получить тот же `409`.
 */
const buttonKind = (hint: ResumeHint, skipsAllFinal: boolean): ButtonKind => {
  if (hint.reason === 'already_resumed' && hint.job_id != null) return 'show-resumed'
  // `nothing_to_resume`: всё сделано, повтор заведомо вернёт 422 — ни кнопки, ни объяснений.
  if (hint.reason === 'job_not_yours' || hint.reason === 'nothing_to_resume' || skipsAllFinal)
    return 'none'
  return hint.available && hint.reason !== 'already_resumed' ? 'continue' : 'retry'
}

/** Подсказка под кнопкой. */
const hintTextOf = (kind: ButtonKind, hint: ResumeHint, errorMessage: string | null) => {
  // `resumeFailureText` знает все коды `hint.reason` из §8, кроме
  // `image_style_required` — тот сознательно оставлен общему `businessView`
  // (внутри `failureText`), который уже переводит его через
  // `imageFailureText`; заводить вторую копию того же текста здесь нельзя.
  // Спрашиваем его пустым `message`, чтобы отличить «нашёл текст» от «не
  // нашёл» (`''` — тоже «не нашёл», отсюда `||`, а не `??`). ВНИМАНИЕ: этот
  // пустой `message` — не универсальный ключ на будущее. Часть таблиц
  // внутри `businessView` (`unknown_image_targets`, `too_many_blocks`,
  // `unknown_blocks`) вклеивает СЕРВЕРНЫЙ `message` в свой
  // текст шаблоном, а часть (`no_image_targets`, `nothing_to_translate`)
  // выбирает ветку регэкспом ПО `message` — на пустой строке шаблон даст
  // обрубок «…: », а регэксп молча возьмёт не ту ветку. Сегодня §8 не
  // присылает ни один из этих кодов в `hint.reason`, но если пришлёт —
  // сначала завести код сюда, в `resumeFailureText`, а не полагаться на
  // этот путь. Фолбэк на `errorMessage` — последним, когда не знает вообще
  // никто. У `show-resumed` своя, не серверная, строка: `already_resumed` с
  // `job_id` — не отказ, а факт («кто-то уже продолжил»), и таблица его не
  // переводит; без `job_id` (данные с бэка противоречивы) кнопка падает в
  // `retry`, и текст для этого случая уже есть в `resumeFailureText`.
  if (kind === 'show-resumed') return messagesNow(messages).alreadyResumed
  if (!hint.reason || hint.reason === 'nothing_to_resume') return null
  return (
    resumeFailureText(hint.reason, errorMessage ?? '') ||
    failureText({ kind: 'business', code: hint.reason, message: '' }).title ||
    errorMessage
  )
}

type ButtonProps = Pick<Props, 'hint' | 'onResume' | 'onShowResumed' | 'isResuming'> & {
  kind: ButtonKind
  isFrozen: boolean
}

/** Кнопка продолжения. На проверке курса «Продолжить»/«Попробовать снова» гаснут
 *  с причиной: продолжение пишет в курс, а бэк отбил бы его только на записи.
 *  «Показать продолжение» остаётся живой — это перецепление без похода в сеть. */
const ResumeButton = ({
  kind,
  hint,
  onResume,
  onShowResumed,
  isResuming,
  isFrozen,
}: ButtonProps) => {
  const m = useMessages(messages)
  if (kind === 'none') return null
  if (kind === 'show-resumed') {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={() => hint.job_id != null && onShowResumed?.(hint.job_id)}
      >
        {m.showResumed}
      </Button>
    )
  }
  const disabled = isResuming || isFrozen
  return (
    <>
      {kind === 'continue' ? (
        <Button size="sm" disabled={disabled} onClick={onResume}>
          {isResuming ? m.resuming : m.resume}
        </Button>
      ) : (
        <Button size="sm" variant="outline" disabled={disabled} onClick={onResume}>
          {m.tryAgain}
        </Button>
      )}
      {isFrozen ? <p className="text-xs text-muted-foreground">{m.underReview}</p> : null}
    </>
  )
}

/**
 * Блок кнопки продолжения (§8) — вынесен из `RunOutcome` отдельным
 * компонентом: у самого итога хватает своих исходов, и ветвление ЭТОГО блока
 * само по себе не маленькое — жить внутри `RunOutcome` оно рвало бы eslint
 * `complexity`.
 */
export const ResumeAction = ({
  hint,
  errorMessage,
  onResume,
  onShowResumed,
  isResuming,
  resumeFailure,
  isFrozen = false,
  skipsAllFinal = false,
}: Props) => {
  const m = useMessages(messages)
  const kind = buttonKind(hint, skipsAllFinal)
  const hintText = hintTextOf(kind, hint, errorMessage)

  return (
    <div className="space-y-1.5 pt-1">
      <ResumeButton
        kind={kind}
        hint={hint}
        onResume={onResume}
        onShowResumed={onShowResumed}
        isResuming={isResuming}
        isFrozen={isFrozen}
      />
      {kind !== 'none' && hint.available && hint.plan_size ? (
        <p className="text-xs text-muted-foreground">{m.stepsLeft(hint.plan_size)}</p>
      ) : null}
      {/* Подсказка гасится отказом клика (финальное ревью раунд 3, минор):
          после подключения resumeFailureText в businessView обе строки
          читают одну и ту же таблицу — на nothing_to_resume автор иначе
          увидел бы один и тот же текст дважды, серым и красным подряд. */}
      {hintText && !resumeFailure ? (
        <p className="text-xs text-muted-foreground">{hintText}</p>
      ) : null}
      {resumeFailure ? (
        <p className="text-xs text-destructive">{failureText(resumeFailure).title}</p>
      ) : null}
    </div>
  )
}
